import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ServiceOptionGrid } from "../components/ServiceOptionGrid";
import { getGeo, countryFromCoords } from "../lib/geo";
import { useLocation as useGeoLocation } from "../lib/location";
import { reverseGeocode } from "../lib/location";
import { searchPlaces } from "../lib/location";
import LiveTrackingMap from "../components/LiveTrackingMap";
import PayPalCardCheckout from "../components/PayPalCardCheckout";
import IosTapTracer from "../components/IosTapTracer";
import ChatPanel from "../components/ChatPanel";
import DriverCard from "../components/DriverCard";
import NearbyDriversMap from "../components/NearbyDriversMap";
import LuxuryVehiclePicker from "../components/LuxuryVehiclePicker";
import LuxuryClassPicker from "../components/LuxuryClassPicker";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SavePlaceChip from "../components/SavePlaceChip";
import SavedPlaceQuickChip from "../components/SavedPlaceQuickChip";
import VehicleTierArt from "../components/VehicleTierArt";
import { formatMoney } from "../lib/money";
import {
  Car, Truck, Wrench, Package, Bike, Disc, Droplets, Bus, Sparkles, Construction, Tag,
  MapPin, Loader2, Send, Navigation, Banknote, CreditCard, Wallet as WalletIcon,
  X, CheckCircle2, Bell, ArrowLeft, Calendar, Plus, Clock, Users, UsersRound, Repeat, ArrowRight, Home, Briefcase,
} from "lucide-react";

// Category → icon + label helper (localised via i18n at render time)
const CATEGORY_META = [
  { key: "taxi",      icon: Car,       accent: "#FFC800" },
  { key: "bus",       icon: Bus,       accent: "#FF7E36" },
  { key: "luxury",    icon: Sparkles,  accent: "#D4AF37" },
  { key: "crane",     icon: Construction,    accent: "#F97316" },
  { key: "towing",    icon: Truck,     accent: "#FF5A1F" },
  { key: "mechanic",  icon: Wrench,    accent: "#22C55E" },
  { key: "tire",      icon: Disc,      accent: "#38BDF8" },
  { key: "carwash",   icon: Droplets,  accent: "#06B6D4" },
  { key: "moving",    icon: Package,   accent: "#A855F7" },
  { key: "delivery",  icon: Bike,      accent: "#EC4899" },
];

// Rough car ETA (min) from linear km at 30 km/h. Used purely for display;
// backend has no real routing yet. Clamp to a minimum of 2 min.
function etaMinutesFromKm(km) {
  if (km == null || Number.isNaN(km)) return null;
  return Math.max(2, Math.round((km / 30) * 60));
}

// Prefer backend-supplied duration_min (from OSRM routing) when available,
// otherwise fall back to straight-line linear estimation.
function tripEtaFromFare(fare, fallbackKm) {
  if (fare && fare.duration_min != null) return Math.max(1, Math.round(fare.duration_min));
  return etaMinutesFromKm(fallbackKm);
}

export default function RideRequest() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const { loc, request, status: locStatus, errorMessage: locErr } = useGeoLocation();
  const [sp] = useSearchParams();
  const preset = sp.get("category");

  const [category, setCategory] = useState(preset || "taxi");
  const [pickup, setPickup] = useState({ address: "", lat: null, lng: null });
  const [dropoff, setDropoff] = useState({ address: "", lat: null, lng: null });
  const [stops, setStops] = useState([]);
  // `paymentMethod` is the UI-selected chip: "cash" | "wallet" | "card"
  //   | "applepay" | "googlepay" | "paypal".
  // Card / Apple Pay / Google Pay / PayPal all submit as `payment_method:
  // "paypal"` to the backend — PayPal Checkout's hosted page renders each
  // funding source as a first-class option on the buyer's device.
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const payMethodForApi = ["card", "applepay", "googlepay"].includes(paymentMethod)
    ? "paypal"
    : paymentMethod;
  const isPaypalFlow = ["card", "applepay", "googlepay", "paypal"].includes(paymentMethod);

  // Feature detection — only surface Apple Pay / Google Pay chips when the
  // current device actually supports them, per user requirement "לא להציג
  // אפשרות כאילו היא פעילה אם היא לא נתמכת".
  const [hasApplePay, setHasApplePay] = useState(false);
  const [hasGooglePay, setHasGooglePay] = useState(false);
  useEffect(() => {
    try {
      // Apple Pay: only Safari on iOS/macOS with a card in Wallet.
      const ap = typeof window !== "undefined" && window.ApplePaySession
        && typeof window.ApplePaySession.canMakePayments === "function"
        && window.ApplePaySession.canMakePayments();
      setHasApplePay(!!ap);
    } catch { setHasApplePay(false); }
    try {
      // Google Pay via PayPal is available on Android Chrome (and modern
      // Chromium desktop with Google Pay set up). We check for the
      // PaymentRequest API + Android UA as a conservative proxy.
      const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
      const gp = typeof window !== "undefined"
        && !!window.PaymentRequest
        && /Android|CrOS/i.test(ua);
      setHasGooglePay(!!gp);
    } catch { setHasGooglePay(false); }
  }, []);
  const [walletBalance, setWalletBalance] = useState(0);
  const [busy, setBusy] = useState(false);
  const [requestedOrder, setRequestedOrder] = useState(null);
  // Set when the customer chose Card in Israel — opens the in-app
  // Advanced Card Fields modal. Cleared on success / cancel / fallback.
  const [pendingCardCheckout, setPendingCardCheckout] = useState(null);
  const [fare, setFare] = useState(null);
  const [fareBusy, setFareBusy] = useState(false);
  const [fareErr, setFareErr] = useState(null);
  const [fareHint, setFareHint] = useState(null);
  const [country, setCountry] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [taxiType, setTaxiType] = useState("standard");
  const [busType, setBusType] = useState("minibus");
  const [craneBand, setCraneBand] = useState("");
  // Crane V1 — customer-picked job duration in hours. Min 2 (first 2h
  // are covered by base_fare); each hour above 2 is billed at the rule's
  // `hourly_extension_rate`. Default 2. Reset when category changes so
  // stale hours don't leak into other categories' payloads.
  const [bookedHours, setBookedHours] = useState(2);
  const [tireOption, setTireOption] = useState("roadside_repair");
  const [tireSize, setTireSize] = useState("");
  const [carwashOption, setCarwashOption] = useState("");
  const [luxuryHireMode, setLuxuryHireMode] = useState("with_driver");
  const [luxuryVehicleId, setLuxuryVehicleId] = useState(null);
  const [luxuryVehicleModel, setLuxuryVehicleModel] = useState("");
  const [luxuryRentalType, setLuxuryRentalType] = useState("per_trip");
  const [luxuryRentalDuration, setLuxuryRentalDuration] = useState(null);
  // Class-based luxury booking (Pricing V2 `luxury_rental` — country
  // rules like executive_sedan / premium_sedan / vip_van). Independent
  // from luxuryVehicleId which points at a specific supplier-owned car.
  const [luxuryVehicleClass, setLuxuryVehicleClass] = useState(null);
  const [luxuryExpectedKm, setLuxuryExpectedKm] = useState(0);
  const [faultNote, setFaultNote] = useState("");
  // Universal driver notes — available across every category
  const [driverNotes, setDriverNotes] = useState("");
  // Delivery address details
  const [deliveryDetails, setDeliveryDetails] = useState({
    apartment: "", floor: "", building: "", gate_code: "",
    instructions: "leave_at_door", contact_phone: "",
  });
  // Discount code
  const [discountCode, setDiscountCode] = useState("");
  const [discountPreview, setDiscountPreview] = useState(null);
  const [discountErr, setDiscountErr] = useState(null);
  const [truckBand, setTruckBand] = useState("");
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [nearestKm, setNearestKm] = useState(null);   // km to closest driver
  const [driversNearby, setDriversNearby] = useState(0);
  const [routeGeometry, setRouteGeometry] = useState(null); // [[lat,lng],...] for map polyline
  const [savedPlaces, setSavedPlaces] = useState([]); // [{label:'Home', address, lat, lng}]
  const [recentPlaces, setRecentPlaces] = useState([]); // deduped from past orders
  const [lastTrip, setLastTrip] = useState(null); // last completed order within 24h
  const [lastTripFrequency, setLastTripFrequency] = useState(0); // # of same trips in last 28 days
  const [lastTripDismissed, setLastTripDismissed] = useState(false);

  // Per-tier fare cache for the taxi & bus pickers. When the DB has
  // option-specific rules (e.g. TR/IL taxi: separate rules for regular,
  // hybrid, 7seats, vip), the pickers cannot derive tier prices client
  // side by multiplying (see iter*: double-multiplication bug). Instead
  // we call /fare/estimate once per tier when pickup/dropoff/route are
  // ready, and each tier card renders its own real price. Keyed by
  // `<category>:<tier_id>` — e.g. `taxi:hybrid` / `bus:minibus`.
  const [tierFares, setTierFares] = useState({});

  const TRUCK_CATS = specs?.truck?.categories || ["moving", "towing"];
  const CRANE_CATS = specs?.crane?.categories || ["crane"];
  const isTaxi = category === "taxi";
  const isBus = category === "bus";
  const isTruck = TRUCK_CATS.includes(category);
  const isCrane = CRANE_CATS.includes(category);
  const isTire = category === "tire";
  const isCarwash = category === "carwash";
  const isMechanic = category === "mechanic";
  const isDelivery = category === "delivery";
  const isMoving = category === "moving";
  const isLuxury = category === "luxury";
  const isTowing = category === "towing";
  // Selected option_code for the shared ServiceOptionGrid (currently
  // used for Towing pilot; other categories follow in Phase 2). Reset
  // when the category chip changes so a stale selection isn't sent.
  const [selectedOption, setSelectedOption] = useState(null);
  useEffect(() => { setSelectedOption(null); setBookedHours(2); }, [category]);

  // Predictive category — smart-suggest based on distance + time-of-day.
  // Moving for long trips, luxury for late-night short trips in central
  // districts, taxi otherwise. Rider can accept in one tap.
  const suggestedCategory = useMemo(() => {
    if (!fare?.distance_km) return null;
    const km = fare.distance_km;
    const hour = new Date().getHours();
    if (km >= 30) return "moving";
    if (km <= 8 && (hour >= 22 || hour < 5)) return "luxury";
    return "taxi";
  }, [fare?.distance_km]);
  const showCategorySuggestion = suggestedCategory && suggestedCategory !== category
    && !isTruck && !isCrane && !isTire && !isCarwash && !isMechanic && !isDelivery;

  const [walletCurrency, setWalletCurrency] = useState("usd");

  // Prefill wallet balance + platform country + vehicle specs
  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/wallet").then((r) => {
        setWalletBalance(r.data.balance || 0);
        setWalletCurrency(r.data.currency || "usd");
      }).catch(() => {});
    }
    api.get("/config").then((r) => setCountry(r.data.country || "US")).catch(() => setCountry("US"));
    api.get("/vehicle-specs").then((r) => {
      // Defensive: iOS Capacitor / WKWebView has been observed handing
      // axios a JSON body as a string when Content-Type is mangled by an
      // intermediate CDN. `setSpecs("{...}")` would leave every downstream
      // `specs?.taxi?.types` as `undefined` and produce the exact
      // symptom the user reports ("service/vehicle option area is
      // completely empty across categories") without any crash. Parse
      // string bodies before storing. No-op when `r.data` is already the
      // expected object (Safari/Web path unchanged).
      let data = r.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { data = null; }
      }
      if (data && typeof data === "object" && !Array.isArray(data)) setSpecs(data);
    }).catch(() => {});
    // Uber-style: fetch Home/Work + recent trip addresses for the dropdown.
    if (user) {
      api.get("/favorites")
        .then((r) => setSavedPlaces(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
      api.get("/my/orders").then((r) => {
        // Same iOS defensive guard as above — `for...of` on a non-array
        // truthy value throws `TypeError: orders is not iterable` inside
        // this .then and is silently swallowed by the outer .catch,
        // leaving `recentPlaces` empty and no Repeat card. Coerce first.
        const orders = Array.isArray(r.data) ? r.data : [];
        // Recent unique addresses
        const seen = new Set();
        const uniq = [];
        for (const o of orders) {
          for (const addr of [o.dropoff_address, o.pickup_address]) {
            if (!addr || seen.has(addr)) continue;
            const lat = addr === o.dropoff_address ? o.dropoff_lat : o.pickup_lat;
            const lng = addr === o.dropoff_address ? o.dropoff_lng : o.pickup_lng;
            if (!lat || !lng) continue;
            seen.add(addr);
            uniq.push({ address: addr, lat, lng });
            if (uniq.length >= 8) break;
          }
          if (uniq.length >= 8) break;
        }
        setRecentPlaces(uniq);
        // Last completed trip within 24h — surface a Repeat card at the top
        // of the sheet so common commuters can re-book in one tap.
        const ONE_DAY = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const last = orders.find((o) => (
          (o.status === "completed" || o.payment_status === "paid" || o.payment_status === "cash_confirmed")
          && o.pickup_lat && o.dropoff_lat && o.pickup_address && o.dropoff_address
          && o.created_at && (now - new Date(o.created_at).getTime()) < ONE_DAY
        ));
        if (last) {
          setLastTrip(last);
          // Count how often the rider makes THIS same trip (same pickup +
          // dropoff coords ≈ same place) over the last 28 days, so the
          // Repeat card can show a "You do this Nx a week" sparkline hint.
          const FOUR_WEEKS = 28 * 24 * 60 * 60 * 1000;
          const sameTrip = (o) =>
            Math.abs((o.pickup_lat || 0) - last.pickup_lat) < 0.005
            && Math.abs((o.pickup_lng || 0) - last.pickup_lng) < 0.005
            && Math.abs((o.dropoff_lat || 0) - last.dropoff_lat) < 0.005
            && Math.abs((o.dropoff_lng || 0) - last.dropoff_lng) < 0.005;
          const freq = orders.filter((o) =>
            o.created_at
            && (now - new Date(o.created_at).getTime()) < FOUR_WEEKS
            && sameTrip(o)
          ).length;
          setLastTripFrequency(freq);
        }
      }).catch(() => {});
    }
  }, [user]);

  // Fare preview whenever category + pickup are set (dropoff optional)
  useEffect(() => {
    if (!category || !pickup.lat || !pickup.lng) {
      setFare(null); setFareErr(null); setFareHint(null); return;
    }
    // P1: Categories wired to ServiceOptionGrid where NO country-level
    // default rule exists (no option_code=None fallback in Pricing V2).
    // The initial /fare/estimate call without an option_code is
    // guaranteed to return pricing_configuration_error and would surface
    // a scary red banner to the user before they've even picked a card.
    // Skip it and show a friendly hint instead — the per-card fares
    // rendered by ServiceOptionGrid are the actual preview.
    const needsOptionFirst =
      (isTowing   && !selectedOption) ||
      (isMoving   && !selectedOption) ||
      (isMechanic && !selectedOption) ||
      (isCarwash  && !carwashOption) ||
      // Crane V1 — a fare preview requires the customer to first pick a
      // boom-height band (option_code). Once picked, the `base_km_hourly`
      // formula is triggered and the preview flows normally.
      (isCrane    && !craneBand) ||
      // Class-based Luxury V2 — until the customer picks a class OR a
      // specific supplier vehicle, there is no fare to preview. The
      // picker itself displays per-class starting rates independently.
      (isLuxury   && !luxuryVehicleClass && !luxuryVehicleId);
    if (needsOptionFirst) {
      setFare(null); setFareErr(null);
      setFareHint(t("request.selectOptionHint", "Choose an option below to see the price"));
      setFareBusy(false);
      return;
    }
    let cancelled = false;
    setFareBusy(true); setFareErr(null); setFareHint(null);
    const payload = {
      category,
      pickup_lat: pickup.lat, pickup_lng: pickup.lng,
      dropoff_lat: dropoff.lat || null, dropoff_lng: dropoff.lng || null,
      stops: (stops || []).filter((s) => s.lat != null && s.lng != null).map((s) => ({ address: s.address || "Stop", lat: s.lat, lng: s.lng })),
      quantity: 1,
      // NOTE: we intentionally do NOT send `country` here. The backend's
      // resolve_country() treats an explicit country as authoritative and
      // skips coord-based detection — sending the platform default ("US")
      // hides the real Istanbul/Tel-Aviv/etc. location and produces a
      // wrong-currency fare. See iter61.
      taxi_type: isTaxi ? taxiType : undefined,
      bus_type: isBus ? busType : undefined,
      // Shared ServiceOptionGrid pilot — Towing, Tire, Mechanic,
      // Delivery, Moving, Carwash all send their selected option_code
      // so per-card prices match the country/category/option Pricing V2
      // rule exactly. Legacy category-specific fields
      // (tire_option / carwash_option) stay on the order payload untouched.
      option_code: isTowing   ? (selectedOption || undefined)
                 : isTire     ? (tireOption     || undefined)
                 : isCarwash  ? (carwashOption  || undefined)
                 : isMechanic ? (selectedOption || undefined)
                 : isDelivery ? (selectedOption || undefined)
                 : isMoving   ? (selectedOption || undefined)
                 : isCrane    ? (craneBand      || undefined)
                 : undefined,
      // Crane V1 — customer's chosen job duration. Sent only for crane;
      // every other category ignores this field. Backend clamps to min 2.
      booked_hours: isCrane ? Number(bookedHours || 2) : undefined,
      use_routing: true,   // Ask backend for OSRM road-distance + duration
      // Phase-3 IL-taxi metered inputs. Ignored by all other formulas.
      // For IL taxi, tariff (Day / Night / Shabbat / Yom Tov) resolves
      // from pickup_at in Israel-local time; booking_source triggers the
      // 4.90 ILS phone/app surcharge under MoT 2026 tariff.
      pickup_at: new Date().toISOString(),
      booking_source: "app",
      // Class-based Luxury (V2 `luxury_rental`). When class + type +
      // duration are all set, backend short-circuits to
      // `_compute_luxury_rental_v2` and ignores taxi/per-km flows.
      luxury_vehicle_class: (isLuxury && luxuryVehicleClass) || undefined,
      luxury_rental_type:   (isLuxury && luxuryVehicleClass && (luxuryRentalType === "hourly" || luxuryRentalType === "daily")) ? luxuryRentalType : undefined,
      luxury_rental_duration: (isLuxury && luxuryVehicleClass && luxuryRentalDuration) ? Number(luxuryRentalDuration) : undefined,
      luxury_expected_km:  (isLuxury && luxuryVehicleClass && luxuryExpectedKm) ? Number(luxuryExpectedKm) : undefined,
    };
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/fare/estimate", payload);
        if (!cancelled) setFare(data);
      } catch (e) {
        if (!cancelled) {
          setFare(null);
          // Backend contract: 422 pricing_configuration_error returns
          // {error, message, country, category, zone_id}. HTTPException from
          // other endpoints uses {detail}. Prefer the specific message when
          // available so the customer sees WHY the fare failed (e.g. "no rule
          // for country=IL category=delivery") instead of a generic string.
          const err = e?.response?.data;
          const msg = (err && typeof err === "object")
            ? (err.message || err.detail || err.error)
            : (typeof err === "string" ? err : null);
          setFareErr(msg || t("request.fareUnavailable", "Pricing not available for this location yet."));
        }
      } finally {
        if (!cancelled) setFareBusy(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [category, pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, country, taxiType, busType, isTaxi, isBus, stops, selectedOption, tireOption, carwashOption, craneBand, bookedHours, luxuryVehicleClass, luxuryRentalType, luxuryRentalDuration, luxuryExpectedKm, luxuryVehicleId]);

  // Per-tier fare fetcher — only fires for taxi & bus categories. Runs
  // once per tier when pickup + dropoff are both set. Populates
  // `tierFares` so each card in the tier picker renders its own real
  // /fare/estimate result instead of a client-side derived multiplier.
  //
  // Fires in parallel (Promise.all) so the picker fills in with one
  // network round trip. Kept independent from the main /fare/estimate
  // above so category/option/stops changes still update the primary
  // fare panel immediately.
  useEffect(() => {
    if (!(isTaxi || isBus)) { setTierFares({}); return; }
    if (!pickup.lat || !pickup.lng || !dropoff.lat || !dropoff.lng) {
      setTierFares({});
      return;
    }
    const tiers = isTaxi ? (specs?.taxi?.types || []) : (specs?.bus?.types || []);
    if (tiers.length === 0) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await Promise.all(tiers.map(async (tt) => {
          const payload = {
            category,
            pickup_lat: pickup.lat, pickup_lng: pickup.lng,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            stops: (stops || []).filter((s) => s.lat != null && s.lng != null)
              .map((s) => ({ address: s.address || "Stop", lat: s.lat, lng: s.lng })),
            quantity: 1,
            taxi_type: isTaxi ? tt.id : undefined,
            bus_type:  isBus  ? tt.id : undefined,
            use_routing: true,
            pickup_at: new Date().toISOString(),
            booking_source: "app",
          };
          try {
            const { data } = await api.post("/fare/estimate", payload);
            return [`${category}:${tt.id}`, { estimate: data?.estimate, currency: data?.currency }];
          } catch {
            return [`${category}:${tt.id}`, null];
          }
        }));
        if (cancelled) return;
        const next = {};
        for (const [key, val] of results) if (val) next[key] = val;
        setTierFares(next);
      } catch { /* swallow — cards fall back to hiding the price */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, isTaxi, isBus, pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, stops, specs]);

  // Fetch OSRM route geometry whenever both endpoints are set so we can
  // draw the pickup → dropoff polyline on the map (Uber-style).
  useEffect(() => {
    if (!pickup.lat || !dropoff.lat) { setRouteGeometry(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const coords = [
          `${pickup.lng},${pickup.lat}`,
          ...(stops || []).filter((s) => s.lat && s.lng).map((s) => `${s.lng},${s.lat}`),
          `${dropoff.lng},${dropoff.lat}`,
        ].join(";");
        const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const geo = data?.routes?.[0]?.geometry?.coordinates;
        if (cancelled || !geo) return;
        // OSRM returns [lng,lat]; NearbyDriversMap expects [lat,lng] tuples
        // (public interface unchanged; the Google Maps rewrite handles the
        // final tuple → LatLngLiteral conversion internally).
        setRouteGeometry(geo.map(([lng, lat]) => [lat, lng]));
      } catch (e) { /* silent fallback — straight line will be used */ }
    })();
    return () => { cancelled = true; };
  }, [pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, stops]);

  // Note: address→coords is now handled by <AddressAutocomplete/> which sets
  // pickup/dropoff/stops atomically when the user picks a suggestion.

  const walletInsufficient = fare ? walletBalance < fare.estimate : false;
  const fmtFare = useMemo(() => (amount) => {
    const cur = (fare?.currency || "usd").toUpperCase();
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(amount); }
    catch (_) { return `${amount.toFixed(2)} ${cur}`; }
  }, [fare?.currency]);

  // Stable pricingContext for ServiceOptionGrid — memoized on the
  // actual pricing-affecting inputs (pickup / dropoff coords) so the
  // reference does NOT change on unrelated re-renders. Previously each
  // ServiceOptionGrid received a fresh object literal that computed
  // `pickup_at: new Date().toISOString()` inline; on iOS Capacitor
  // (slower TLS + more re-renders) this caused ServiceOptionGrid's
  // per-option /fare/estimate loop to re-fire on every render, so the
  // customer saw prices flicker as partial state was written to
  // priceByOption while newer requests were still resolving. On Safari
  // desktop the requests resolved fast enough that the churn was
  // invisible. Memoizing preserves EXACT same payload semantics — the
  // pickup_at value is still an ISO timestamp captured the first time
  // pickup+dropoff coords are set — but stops the per-render churn.
  const optionPricingContext = useMemo(() => {
    if (!pickup?.lat || !dropoff?.lat) return undefined;
    return {
      pickup_lat: pickup.lat, pickup_lng: pickup.lng,
      dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
      quantity: 1,
      pickup_at: new Date().toISOString(),
      booking_source: "app",
    };
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);


  // Prefill pickup from GPS
  useEffect(() => {
    if (loc && loc.lat && loc.lng && !pickup.lat) {
      setPickup({ address: loc.label || `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`, lat: loc.lat, lng: loc.lng });
    }
    // eslint-disable-next-line
  }, [loc]);

  const useMyLocation = async () => {
    const p = await request();
    if (p?.lat && p?.lng) {
      setPickup({ address: p.label || `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`, lat: p.lat, lng: p.lng });
      return;
    }
    // Differentiated error UX. Never silently guess a country / never fall
    // back to IP location — see currency architecture rules. Each state
    // gets its own translated message so the user knows exactly what to do.
    const s = locStatus;
    const key =
      s === "denied"      ? "request.locDenied"      :
      s === "unavailable" ? "request.locUnavailable" :
      s === "timeout"     ? "request.locTimeout"     :
      s === "insecure"    ? "request.locInsecure"    :
                            "request.locGeneric";
    const fallback =
      s === "denied"      ? "Location permission denied. Enable it in your device / browser settings, then tap again." :
      s === "unavailable" ? "Your device could not get a GPS fix. Try again outdoors or enter your address manually." :
      s === "timeout"     ? "Location request timed out. Please try again." :
      s === "insecure"    ? "Geolocation requires HTTPS. Please reload over a secure connection." :
                            (locErr || "Could not get your location. Please enter your pickup address.");
    toast.error(t(key, fallback));
  };

  const submit = async () => {
    if (!category) return toast.error(t("request.pickCategory", "Pick a service category"));
    if (!pickup.lat || !pickup.lng) return toast.error(t("request.needPickup", "Set your pickup location"));
    let sched_iso;
    if (scheduleMode === "later") {
      if (!scheduledAt) return toast.error(t("request.pickWhen", "Pick a date & time"));
      const d = new Date(scheduledAt);
      if (isNaN(d.getTime())) return toast.error(t("request.pickWhen", "Pick a date & time"));
      if (d.getTime() < Date.now() + 5 * 60 * 1000) {
        return toast.error(t("request.pickFuture", "Pick a time at least 5 minutes from now"));
      }
      sched_iso = d.toISOString();
    }
    setBusy(true);
    try {
      const payload = {
        category,
        pickup_address: pickup.address || "Current location",
        pickup_lat: pickup.lat,
        pickup_lng: pickup.lng,
        dropoff_address: dropoff.address || null,
        dropoff_lat: dropoff.lat || null,
        dropoff_lng: dropoff.lng || null,
        stops: (stops || []).filter((s) => s.lat != null && s.lng != null).map((s) => ({ address: s.address || "Stop", lat: s.lat, lng: s.lng })),
        payment_method: payMethodForApi,
        quantity: 1,
        taxi_type: isTaxi ? taxiType : undefined,
        bus_type: isBus ? busType : undefined,
        min_truck_tonnage_band: isTruck && truckBand ? truckBand : undefined,
        min_crane_tonnage_band: isCrane && craneBand ? craneBand : undefined,
        // Crane V1 — persist the customer's chosen duration with the
        // order so the supplier sees the requested job length.
        booked_hours: isCrane ? Number(bookedHours || 2) : undefined,
        tire_option: isTire ? tireOption : undefined,
        tire_size: isTire && tireSize ? tireSize : undefined,
        carwash_option: isCarwash ? carwashOption : undefined,
        luxury_hire_mode: isLuxury ? luxuryHireMode : undefined,
        luxury_vehicle_id: isLuxury && luxuryVehicleId && !String(luxuryVehicleId).startsWith("supplier:") ? luxuryVehicleId : undefined,
        luxury_vehicle_model: isLuxury && luxuryVehicleModel ? luxuryVehicleModel : undefined,
        luxury_vehicle_class: isLuxury && luxuryVehicleClass ? luxuryVehicleClass : undefined,
        luxury_rental_type: isLuxury && luxuryRentalType !== "per_trip" ? luxuryRentalType : undefined,
        luxury_rental_duration: isLuxury && luxuryRentalType !== "per_trip" && luxuryRentalDuration ? luxuryRentalDuration : undefined,
        luxury_expected_km: isLuxury && luxuryVehicleClass && luxuryExpectedKm ? Number(luxuryExpectedKm) : undefined,
        fault_note: isMechanic && faultNote ? faultNote : undefined,
        driver_notes: driverNotes ? driverNotes.slice(0, 500) : undefined,
        delivery_apartment: isDelivery && deliveryDetails.apartment ? deliveryDetails.apartment : undefined,
        delivery_floor: isDelivery && deliveryDetails.floor ? deliveryDetails.floor : undefined,
        delivery_building: isDelivery && deliveryDetails.building ? deliveryDetails.building : undefined,
        delivery_gate_code: isDelivery && deliveryDetails.gate_code ? deliveryDetails.gate_code : undefined,
        delivery_instructions: isDelivery ? deliveryDetails.instructions : undefined,
        delivery_contact_phone: isDelivery && deliveryDetails.contact_phone ? deliveryDetails.contact_phone : undefined,
        discount_code: discountPreview ? discountCode.trim().toUpperCase() : undefined,
        scheduled_at: sched_iso,
      };
      const { data: order } = await api.post("/orders", payload);
      // ACDC In-app Advanced Card Fields — Israel + Turkey + UAE.
      // TR / AE flow through PayPal via automatic TRY/AED → USD FX
      // conversion, exactly like the existing redirect checkout. Any
      // other country (or a server-side ACDC downgrade) falls back to
      // the redirect flow via the modal's onFallback handler.
      //
      // Country source: use `order.country` (server-resolved from the
      // actual pickup coords) rather than the platform-config `country`
      // state which only reflects the deployment default. That state
      // returns "US" for a fresh session in Istanbul, which incorrectly
      // routed TR customers back to the redirect flow (bug seen 2026-09
      // Preview: TR user landed on paypal.com instead of the modal).
      const orderCountry = (order?.country || country || "").toUpperCase();
      if (paymentMethod === "card" && ["IL", "TR", "AE"].includes(orderCountry)) {
        setPendingCardCheckout({
          orderId: order.order_id,
          amount: order.total || fare?.estimate || 0,
          currency: order.currency || fare?.currency || "ILS",
        });
        return;
      }
      if (isPaypalFlow) {
        try {
          // For non-native-PayPal currencies (TRY, AED, SAR, INR, …) we must
          // first fetch a quote, show the customer the USD conversion, and
          // proceed only after they explicitly accept. For USD/ILS/EUR etc.
          // the quote returns will_convert:false and we skip the extra step.
          let quote = null;
          try {
            const { data: q } = await api.get(`/payments/paypal/quote/${order.order_id}`);
            if (q.will_convert) quote = q;
          } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.code === "fx_unavailable") {
              toast.error(t("request.fxUnavailable",
                "Exchange-rate service unavailable. Please choose another payment method."));
              setPaymentMethod("cash");
              return;
            }
          }
          if (quote) {
            const fx = Number(quote.fx_rate);
            const orig = quote.original;
            const pp   = quote.paypal;
            const rateLabel = fx < 1
              ? `1 ${orig.currency} ≈ ${fx.toFixed(4)} ${pp.currency}`
              : `1 ${pp.currency} ≈ ${(1/fx).toFixed(4)} ${orig.currency}`;
            const msg =
              `${t("request.paypalConvert.title", "PayPal will charge in USD")}\n\n` +
              `${t("request.paypalConvert.fare", "Your fare")}: ${orig.amount.toFixed(2)} ${orig.currency}\n` +
              `${t("request.paypalConvert.charge", "PayPal charge")}: ${pp.amount.toFixed(2)} ${pp.currency}\n` +
              `${t("request.paypalConvert.rate", "Rate")}: ${rateLabel}\n` +
              `${t("request.paypalConvert.source", "Source")}: ${quote.fx_source}\n\n` +
              `${t("request.paypalConvert.confirm", "Continue with this conversion?")}`;
            if (!window.confirm(msg)) {
              toast(t("request.paypalConvert.cancelled",
                "Payment cancelled. Pick another method or try again."));
              return;
            }
          }
          const { data: pp } = await api.post("/payments/paypal/create", {
            order_id: order.order_id,
            origin_url: window.location.origin,
            confirmed_conversion: !!quote,
          });
          window.location.assign(pp.approve_url);
          return;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.code === "paypal_currency_unsupported") {
            toast.error(t("request.paypalCurrencyUnsupported",
              "PayPal isn't available for {{cur}} payments. Please choose another payment method.",
              { cur: detail.currency }));
            setPaymentMethod("cash");
            return;
          }
          toast.error(formatApiError(e));
          return;
        }
      }
      if (order.status === "scheduled") {
        toast.success(t("request.scheduled", "Scheduled — we'll dispatch a driver near your booking time"));
        nav("/activity");
        return;
      }
      toast.success(t("request.sent", "Request sent — looking for a driver…"));
      setRequestedOrder(order);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (requestedOrder) {
    return <DispatchingScreen order={requestedOrder} onCancelled={() => setRequestedOrder(null)} />;
  }

  const mapCenter = pickup.lat && pickup.lng ? [pickup.lat, pickup.lng] : null;
  const nearestEta = etaMinutesFromKm(nearestKm);

  return (
    <div className="fixed inset-0 bg-obsidian overflow-hidden" data-testid="ride-request-page">
      {/* Fullscreen map (background layer) */}
      <div className="absolute inset-0 z-0" data-testid="ride-map-layer">
        <NearbyDriversMap
          category={category}
          center={mapCenter}
          height="100%"
          fullscreen
          darkTiles
          taxiType={isTaxi ? taxiType : null}
          minTruckTonnageBand={isTruck ? (truckBand || null) : null}
          onDriversChange={(drivers) => {
            setDriversNearby(drivers.length);
            setNearestKm(drivers.length ? drivers[0]?.distance_km ?? null : null);
          }}
          pickup={pickup.lat ? { lat: pickup.lat, lng: pickup.lng } : null}
          dropoff={dropoff.lat ? { lat: dropoff.lat, lng: dropoff.lng } : null}
          routeGeometry={routeGeometry}
          onPickupDrag={async ({ lat, lng }) => {
            // Optimistic: set coords immediately so the fare recomputes.
            setPickup((p) => ({ ...p, lat, lng, address: "Adjusting…" }));
            const label = await reverseGeocode(lat, lng);
            setPickup({
              address: label || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
              lat, lng,
            });
            toast.success(t("request.pinAdjusted", "Pickup updated"));
          }}
        />
        {/* Vignette + grain over map */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-obsidian/70 via-transparent to-obsidian" />
        <div className="grain-overlay absolute inset-0" />
      </div>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between p-4 pointer-events-none">
        <button
          onClick={() => nav(-1)}
          className="pointer-events-auto w-11 h-11 rounded-full bg-black/70 backdrop-blur-md border border-white/10 flex items-center justify-center text-white active:scale-95 transition-transform"
          data-testid="ride-back-btn"
          aria-label={t("request.back", "Back")}
        >
          <ArrowLeft size={18} className="rtl:rotate-180" />
        </button>
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-md border border-white/10 px-3 py-2 text-[12px] font-semibold text-white tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
          <span data-testid="drivers-nearby-pill">
            {driversNearby} {driversNearby === 1 ? t("request.driverNearby", "driver nearby") : t("request.driversNearby", "drivers nearby")}
          </span>
        </div>
        <button
          onClick={useMyLocation}
          className="pointer-events-auto w-11 h-11 rounded-full bg-black/70 backdrop-blur-md border border-white/10 flex items-center justify-center text-white active:scale-95 transition-transform"
          data-testid="use-my-location"
          aria-label={t("request.gps", "GPS")}
        >
          <Navigation size={17} />
        </button>
      </div>

      {/* Trip badge — floating pill on the map when both endpoints exist. */}
      {pickup.lat && dropoff.lat && fare?.distance_km != null && (
        <div
          className="absolute z-30 top-[74px] inset-x-0 flex justify-center pointer-events-none"
          data-testid="trip-badge-wrap"
        >
          <div
            className="pointer-events-auto inline-flex items-center gap-3 rounded-full bg-black/80 backdrop-blur-md border border-white/10 px-4 py-2 text-white shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
            data-testid="trip-badge"
          >
            <span className="inline-flex items-center gap-1.5 text-[13px] font-bold tabular-nums">
              <Navigation size={12} className="text-brand-accent" />
              {fare.distance_km.toFixed(1)} km
            </span>
            {fare.duration_min != null && (
              <>
                <span className="text-white/25">·</span>
                <span className="inline-flex items-center gap-1.5 text-[13px] font-bold tabular-nums">
                  <Clock size={12} className="text-brand-accent" />
                  {Math.max(1, Math.round(fare.duration_min))} min
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pin-drag hint — teaches riders they can fine-tune the pickup by
          dragging the white pin on the map. Dismissible + shown only when
          pickup has coords but the sheet hasn't been used for adjustments yet. */}
      {pickup.lat && !dropoff.lat && (
        <div className="absolute z-30 top-[120px] inset-x-0 flex justify-center pointer-events-none px-4" data-testid="pin-drag-hint-wrap">
          <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-black/80 backdrop-blur-md border border-white/10 px-3 py-1.5 text-[11px] font-semibold text-white/80 shadow-[0_4px_16px_rgba(0,0,0,0.4)]" data-testid="pin-drag-hint">
            <MapPin size={11} className="text-brand-accent" />
            {t("request.pinDragHint", "Drag the pin to fine-tune your pickup")}
          </div>
        </div>
      )}

      {/* Bottom sheet */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 bg-obsidian/95 backdrop-blur-2xl border-t border-white/10 rounded-t-[2rem] shadow-[0_-12px_48px_rgba(0,0,0,0.6)] animate-sheet-up flex flex-col"
        style={{ maxHeight: "85dvh" }}
        data-testid="booking-sheet"
      >
        <div className="sheet-handle" aria-hidden />
        <div className="overflow-y-auto overscroll-contain px-4 pt-1 pb-3 hide-scrollbar">
          {/* Header row */}
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight leading-none">
                {t("request.title", "Request a service")}
              </h1>
              <div className="text-[12px] text-white/50 mt-1.5">
                {t("request.eyebrow", "On-demand")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {category === "taxi" && (
                <Link
                  to={`/shared${pickup.address || dropoff.address ? `?pickup=${encodeURIComponent(pickup.address || "")}&dropoff=${encodeURIComponent(dropoff.address || "")}` : ""}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-white/[0.06] border border-white/10 hover:bg-white/[0.1] transition-colors rounded-full px-3 py-1.5"
                  data-testid="ride-shared-cta"
                >
                  <UsersRound size={12} strokeWidth={2.5} /> {t("request.shareRide", "Share ride")}
                </Link>
              )}
              {nearestEta != null && (
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-accent bg-brand-accent/10 border border-brand-accent/25 rounded-full px-2.5 py-1" data-testid="nearest-eta-badge">
                  <Clock size={11} /> {nearestEta} {t("request.minShort", "min")}
                </div>
              )}
            </div>
          </div>

          {/* Category chip row */}
          <div className="-mx-4 px-4 mb-4">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar snap-x">
              {CATEGORY_META.map((c) => {
                const Icon = c.icon;
                const selected = category === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className={`snap-start whitespace-nowrap inline-flex items-center gap-2 rounded-full px-4 py-2 border transition-all duration-200 active:scale-[0.98] ${
                      selected
                        ? "bg-white text-obsidian border-white shadow-[0_0_16px_rgba(255,255,255,0.2)]"
                        : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/25 hover:text-white"
                    }`}
                    data-testid={`category-${c.key}`}
                  >
                    <Icon size={15} style={{ color: selected ? "#050505" : c.accent }} />
                    <span className="text-[12px] font-semibold tracking-tight">{t(`categories.${c.key}`, c.key)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Repeat Last Trip — Uber-style one-tap re-book when a completed
              trip exists within 24h. Loads pickup/dropoff instantly so the
              rider can adjust seats/category or just tap Request. */}
          {lastTrip && !lastTripDismissed && !pickup.lat && !dropoff.lat && (
            <div className="mb-3 rounded-2xl border border-electric-teal/30 bg-electric-teal/[0.06] p-3 flex items-center gap-3" data-testid="repeat-last-trip-card">
              <div className="w-10 h-10 rounded-full bg-electric-teal text-obsidian flex items-center justify-center shrink-0">
                <Repeat size={16} strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal inline-flex items-center gap-1.5">
                  {t("request.repeatEyebrow", "Take this trip again?")}
                  {lastTripFrequency >= 2 && (
                    <span
                      className="inline-flex items-center gap-0.5 rounded-full bg-electric-teal/20 text-electric-teal border border-electric-teal/30 px-1.5 py-[1px] text-[9px] font-black tabular-nums normal-case tracking-normal"
                      data-testid="repeat-frequency-badge"
                      title={t("request.freqTooltip", "How often you take this trip")}
                    >
                      {lastTripFrequency >= 12
                        ? t("request.freqDaily", "daily habit")
                        : lastTripFrequency >= 4
                          ? t("request.freqWeekly", "{{n}}× a week", { n: Math.round(lastTripFrequency / 4) })
                          : t("request.freqMonth", "{{n}}× this month", { n: lastTripFrequency })}
                    </span>
                  )}
                </div>
                <div className="text-[12px] font-bold text-white truncate">{(lastTrip.pickup_address || "").split(",")[0]} → {(lastTrip.dropoff_address || "").split(",")[0]}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPickup({ address: lastTrip.pickup_address, lat: lastTrip.pickup_lat, lng: lastTrip.pickup_lng });
                  setDropoff({ address: lastTrip.dropoff_address, lat: lastTrip.dropoff_lat, lng: lastTrip.dropoff_lng });
                  if (lastTrip.category) setCategory(lastTrip.category);
                }}
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-electric-teal text-obsidian px-3 py-1.5 text-[11px] font-black hover:bg-electric-teal/90 transition"
                data-testid="repeat-last-trip-btn"
              >
                {t("request.repeatBtn", "Go")} <ArrowRight size={12} strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={() => setLastTripDismissed(true)}
                className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10"
                data-testid="repeat-last-trip-dismiss"
                aria-label={t("request.dismiss", "Dismiss")}
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Location inputs */}
          <div className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-3 pl-4 mb-3" data-testid="location-inputs">
            {/* Connector line */}
            <div className="absolute start-6 top-8 bottom-8 w-px bg-white/15" />
            {/* Pickup */}
            <div className="flex items-center gap-3 py-2" data-testid="pickup-row">
              <div className="w-2.5 h-2.5 rounded-sm bg-white shadow-[0_0_0_3px_rgba(255,255,255,0.12)] flex-shrink-0" />
              <AddressAutocomplete
                value={pickup.address}
                onChange={(v) => setPickup({ address: v, lat: null, lng: null })}
                onSelect={(sel) => setPickup(sel)}
                placeholder={t("request.pickupPh", "Pickup location")}
                testid="pickup-input"
                hasCoords={!!pickup.lat}
                showCurrentLocation
                onUseCurrentLocation={useMyLocation}
                savedPlaces={savedPlaces}
                recentPlaces={recentPlaces}
                trailingSlot={pickup.lat && (
                  <>
                    <SavePlaceChip
                      address={pickup.address}
                      lat={pickup.lat}
                      lng={pickup.lng}
                      existing={savedPlaces}
                      onSaved={() => api.get("/favorites").then((r) => setSavedPlaces(r.data || [])).catch(() => {})}
                      testid="save-pickup"
                    />
                    <span className="text-[10px] font-mono text-white/40 hidden md:inline" data-testid="pickup-coords">
                      {pickup.lat.toFixed(3)}, {pickup.lng.toFixed(3)}
                    </span>
                  </>
                )}
              />
            </div>
            {/* Intermediate stops */}
            {stops.map((stop, idx) => (
              <div key={idx} className="flex items-center gap-3 py-2 border-t border-white/5" data-testid={`stop-row-${idx}`}>
                <div className="w-2.5 h-2.5 rounded-full bg-amber-gold flex-shrink-0" />
                <AddressAutocomplete
                  value={stop.address}
                  onChange={(v) => setStops((prev) => prev.map((s, i) => i === idx ? { address: v, lat: null, lng: null } : s))}
                  onSelect={(sel) => setStops((prev) => prev.map((s, i) => i === idx ? sel : s))}
                  placeholder={t("request.stopPh", "Stop address")}
                  testid={`stop-address-${idx}`}
                  hasCoords={!!stop.lat}
                />
                <button
                  type="button"
                  onClick={() => setStops((prev) => prev.filter((_, i) => i !== idx))}
                  className="text-white/50 hover:text-red-400 p-1"
                  data-testid={`remove-stop-${idx}`}
                  aria-label={t("request.removeStop", "Remove stop")}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {/* Dropoff */}
            <div className="flex items-center gap-3 py-2 border-t border-white/5" data-testid="dropoff-row">
              <div className="w-2.5 h-2.5 rounded-full bg-electric-teal shadow-[0_0_0_3px_rgba(0,229,255,0.15)] flex-shrink-0" />
              <AddressAutocomplete
                value={dropoff.address}
                onChange={(v) => setDropoff({ address: v, lat: null, lng: null })}
                onSelect={(sel) => setDropoff(sel)}
                placeholder={t("request.dropoffPh", "Where to?")}
                testid="dropoff-input"
                hasCoords={!!dropoff.lat}
                savedPlaces={savedPlaces}
                recentPlaces={recentPlaces}
                trailingSlot={dropoff.lat && (
                  <SavePlaceChip
                    address={dropoff.address}
                    lat={dropoff.lat}
                    lng={dropoff.lng}
                    existing={savedPlaces}
                    onSaved={() => api.get("/favorites").then((r) => setSavedPlaces(r.data || [])).catch(() => {})}
                    testid="save-dropoff"
                  />
                )}
              />
              {stops.length < 4 && (
                <button
                  type="button"
                  onClick={() => setStops((s) => [...s, { address: "", lat: null, lng: null }])}
                  className="text-[10px] uppercase font-bold text-electric-teal inline-flex items-center gap-1 tracking-wide"
                  data-testid="add-stop-btn"
                >
                  <Plus size={11} /> {t("request.addStop", "Stop")}
                </button>
              )}
            </div>
          </div>
          {/* Home & Work quick chips — one-tap fill for the two most common
              destinations. Fills pickup if empty, dropoff otherwise. Each
              chip carries an inline gear editor (rename / delete). */}
          {savedPlaces.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2" data-testid="quick-place-chips">
              {savedPlaces.filter((p) => /^(home|work)$/i.test(p.label || "")).slice(0, 4).map((p) => (
                <SavedPlaceQuickChip
                  key={p.favorite_id}
                  place={p}
                  testid={`quick-chip-${p.label.toLowerCase()}`}
                  onFill={() => {
                    const setter = !pickup.lat ? setPickup : setDropoff;
                    setter({ address: p.address, lat: p.lat, lng: p.lng });
                  }}
                  onChanged={() =>
                    api.get("/favorites").then((r) => setSavedPlaces(r.data || [])).catch(() => {})
                  }
                />
              ))}
            </div>
          )}
          {/* Predictive category — nudges the rider toward the smarter
              service for their trip (long distance → freight, late-night
              short → luxury, etc). One tap swaps the category. */}
          {showCategorySuggestion && (
            <button
              type="button"
              onClick={() => setCategory(suggestedCategory)}
              className="mb-3 w-full flex items-center gap-3 rounded-2xl border border-amber-gold/30 bg-amber-gold/[0.06] hover:bg-amber-gold/[0.12] transition px-3 py-2.5 text-start"
              data-testid="predict-category-chip"
            >
              <div className="w-8 h-8 rounded-full bg-amber-gold text-obsidian flex items-center justify-center shrink-0">
                <Sparkles size={14} strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-gold">{t("request.smartSuggest", "Smart suggestion")}</div>
                <div className="text-[12px] font-bold text-white">
                  {t("request.suggestSwitchTo", "Try {{cat}} for this trip →", { cat: t(`categories.${suggestedCategory}`, suggestedCategory) })}
                </div>
              </div>
              <ArrowRight size={14} className="text-amber-gold shrink-0" />
            </button>
          )}

          {/* When: Now vs Reserve */}
          <div className="inline-flex bg-white/[0.04] border border-white/10 rounded-full p-1 mb-4" data-testid="schedule-picker">
            <button
              type="button"
              onClick={() => { setScheduleMode("now"); setScheduledAt(""); }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${scheduleMode === "now" ? "bg-white text-obsidian" : "text-white/70 hover:text-white"}`}
              data-testid="schedule-now"
            >
              {t("request.now", "Now")}
            </button>
            <button
              type="button"
              onClick={() => setScheduleMode("later")}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${scheduleMode === "later" ? "bg-amber-gold text-obsidian shadow-gold" : "text-white/70 hover:text-white"}`}
              data-testid="schedule-later"
            >
              <Calendar size={11} /> {t("request.reserve", "Reserve")}
            </button>
          </div>
          {scheduleMode === "later" && (
            <div className="-mt-2 mb-3 space-y-3" data-testid="schedule-later-panel">
              {/* Quick presets — most riders pick one of these */}
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const now = new Date();
                  // Round DOWN to next 5-min boundary for a clean base
                  const base = new Date(Math.ceil(now.getTime() / (5 * 60 * 1000)) * 5 * 60 * 1000);
                  const preset = (mins, labelKey, labelDefault) => {
                    const d = new Date(base.getTime() + mins * 60 * 1000);
                    return { key: `p${mins}`, label: t(labelKey, labelDefault), iso: d.toISOString().slice(0, 16) };
                  };
                  const tomorrowAt = (hour, minute = 0) => {
                    const d = new Date();
                    d.setDate(d.getDate() + 1);
                    d.setHours(hour, minute, 0, 0);
                    return { key: `tm${hour}`, label: `${t("request.tomorrow", "Tomorrow")} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`, iso: d.toISOString().slice(0, 16) };
                  };
                  const presets = [
                    preset(30, "request.in30", "In 30 min"),
                    preset(60, "request.in1h", "In 1 hour"),
                    preset(120, "request.in2h", "In 2 hours"),
                    tomorrowAt(9),
                    tomorrowAt(18),
                  ];
                  return presets.map((p) => {
                    const selected = scheduledAt === p.iso;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setScheduledAt(p.iso)}
                        className={`rounded-full px-3 py-1.5 text-[11px] font-black transition border ${
                          selected
                            ? "bg-amber-gold text-obsidian border-amber-gold shadow-gold"
                            : "bg-white/[0.04] text-white/80 border-white/10 hover:border-amber-gold/40 hover:text-white"
                        }`}
                        data-testid={`schedule-preset-${p.key}`}
                      >
                        {p.label}
                      </button>
                    );
                  });
                })()}
              </div>
              {/* Custom picker */}
              <div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-white/40 mb-1">
                  {t("request.orPickCustom", "Or pick a custom time")}
                </div>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-3 text-sm text-white outline-none focus:border-amber-gold"
                  data-testid="schedule-datetime"
                />
              </div>
              {scheduledAt && (
                <div className="rounded-xl bg-amber-gold/10 border border-amber-gold/30 px-3 py-2 text-[11px] text-amber-gold font-semibold inline-flex items-center gap-1.5" data-testid="schedule-summary">
                  <Calendar size={11} />
                  {(() => {
                    try {
                      const d = new Date(scheduledAt);
                      return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                    } catch { return scheduledAt; }
                  })()}
                </div>
              )}
              <div className="text-[10px] text-white/50 ms-1">
                {t("request.scheduleDispatchHint", "We'll start dispatching drivers ~10 min before your booking.")}
              </div>
            </div>
          )}

          {/* Truck tonnage picker (for truck categories — Towing + Moving
              now use the shared ServiceOptionGrid below, so hide the
              pill picker for those categories to avoid UI duplication.
              Kept intact for `transport`). */}
          {isTruck && !isTowing && !isMoving && (
            <div className="mb-4" data-testid="truck-band-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.pickTruckBand", "Minimum truck capacity")}</div>
              <div className="flex flex-wrap gap-2">
                {[{ id: "", label: t("request.anyCapacity", "Any") }, ...((specs?.truck?.bands) || [])].map((b) => {
                  const selected = truckBand === b.id;
                  return (
                    <button
                      key={b.id || "any"}
                      onClick={() => setTruckBand(b.id)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${selected ? "bg-amber-gold text-obsidian border-amber-gold shadow-gold" : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/25"}`}
                      data-testid={`truck-band-${b.id || "any"}`}
                    >
                      {b.id ? t(`truck.${b.id}`, b.id) : b.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vehicle tier cards (taxi only) */}
          {isTaxi && (
            <div className="mb-4" data-testid="taxi-tier-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.pickTaxiClass", "Choose a ride")}</div>
              {pickup.lat && fareErr && (
                <div className="mb-2 text-[12px] text-brand-warn bg-brand-warn/10 border border-brand-warn/30 rounded-xl px-3 py-2" data-testid="taxi-fare-error">
                  {fareErr}
                </div>
              )}
              <div className="-mx-4 px-4">
                <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x pb-1">
                  {(specs?.taxi?.types || []).map((tt) => {
                    const selected = taxiType === tt.id;
                    // Prefer the per-tier /fare/estimate value (correct
                    // for DBs with option_code-specific rules). Fall
                    // back to the client-side derived value while the
                    // per-tier fetch is still in flight so the picker
                    // never shows an empty slot.
                    const fetched = tierFares[`taxi:${tt.id}`]?.estimate;
                    const baseEstimate = fare && fare.breakdown?.taxi_multiplier
                      ? fare.estimate / fare.breakdown.taxi_multiplier
                      : fare?.estimate;
                    const derived = baseEstimate ? baseEstimate * tt.multiplier : null;
                    const tierFare = (typeof fetched === "number") ? fetched : derived;
                    const tierEta = nearestEta != null ? nearestEta + (tt.id === "vip" ? 1 : 0) : null;
                    const isPremium = tt.id === "vip" || tt.id === "family";
                    return (
                      <button
                        key={tt.id}
                        onClick={() => setTaxiType(tt.id)}
                        className={`snap-center flex-shrink-0 w-[128px] rounded-2xl p-2.5 text-start border transition-all duration-200 relative overflow-hidden ${
                          selected
                            ? "bg-amber-gold/[0.08] border-amber-gold ring-gold-glow scale-[1.02]"
                            : "bg-white/[0.04] border-white/10 hover:border-white/25"
                        }`}
                        data-testid={`taxi-tier-${tt.id}`}
                      >
                        {isPremium && (
                          <div className="absolute top-1.5 end-1.5 text-[8px] font-bold uppercase tracking-widest text-amber-gold flex items-center gap-0.5">
                            <Sparkles size={8} /> {tt.id === "vip" ? "VIP" : t("taxi.family", "Family")}
                          </div>
                        )}
                        <div className="h-11 flex items-center justify-center">
                          <VehicleTierArt tier={tt.id} selected={selected} size={56} />
                        </div>
                        <div className="text-[12px] font-bold text-white truncate leading-tight">{t(`taxi.${tt.id}`, tt.id)}</div>
                        <div className="text-[10px] text-white/50 flex items-center gap-1 mt-0.5">
                          <Users size={9} /> {tt.seats} · ×{tt.multiplier.toFixed(1)}
                        </div>
                        {tierFare != null && (
                          <div className="mt-1.5 text-[13px] font-bold text-white tabular-nums" data-testid={`taxi-tier-fare-${tt.id}`}>
                            {fmtFare(Number(tierFare.toFixed(2)))}
                          </div>
                        )}
                        {tierEta != null && (
                          <div className="text-[10px] text-electric-teal/90 mt-0.5" data-testid={`taxi-tier-eta-${tt.id}`}>
                            {tierEta} {t("request.minShort", "min")} {t("request.away", "away")}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Bus tier cards (bus only) */}
          {isBus && (
            <div className="mb-4" data-testid="bus-tier-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.pickBusClass", "Choose a bus")}</div>
              {pickup.lat && fareErr && (
                <div className="mb-2 text-[12px] text-brand-warn bg-brand-warn/10 border border-brand-warn/30 rounded-xl px-3 py-2" data-testid="bus-fare-error">
                  {fareErr}
                </div>
              )}
              <div className="-mx-4 px-4">
                <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x pb-1">
                  {(specs?.bus?.types || []).map((bt) => {
                    const selected = busType === bt.id;
                    const fetched = tierFares[`bus:${bt.id}`]?.estimate;
                    const baseEstimate = fare && fare.breakdown?.bus_multiplier
                      ? fare.estimate / fare.breakdown.bus_multiplier
                      : fare?.estimate;
                    const derived = baseEstimate ? baseEstimate * bt.multiplier : null;
                    const tierFare = (typeof fetched === "number") ? fetched : derived;
                    const tierEta = nearestEta != null ? nearestEta + (bt.id === "bus_standard" ? 4 : 2) : null;
                    return (
                      <button
                        key={bt.id}
                        onClick={() => setBusType(bt.id)}
                        className={`snap-center flex-shrink-0 w-[128px] rounded-2xl p-2.5 text-start border transition-all duration-200 relative overflow-hidden ${
                          selected
                            ? "bg-amber-gold/[0.08] border-amber-gold ring-gold-glow scale-[1.02]"
                            : "bg-white/[0.04] border-white/10 hover:border-white/25"
                        }`}
                        data-testid={`bus-tier-${bt.id}`}
                      >
                        <div className="h-11 flex items-center justify-center">
                          <Bus size={36} strokeWidth={1.4} style={{ color: selected ? "#FFC800" : "#E5E5E5" }} />
                        </div>
                        <div className="text-[12px] font-bold text-white truncate leading-tight">{t(`bus.${bt.id}`, bt.id)}</div>
                        <div className="text-[10px] text-white/50 flex items-center gap-1 mt-0.5">
                          <Users size={9} /> {bt.seats} · ×{bt.multiplier.toFixed(1)}
                        </div>
                        {tierFare != null && (
                          <div className="mt-1.5 text-[13px] font-bold text-white tabular-nums" data-testid={`bus-tier-fare-${bt.id}`}>
                            {fmtFare(Number(tierFare.toFixed(2)))}
                          </div>
                        )}
                        {tierEta != null && (
                          <div className="text-[10px] text-electric-teal/90 mt-0.5" data-testid={`bus-tier-eta-${bt.id}`}>
                            {tierEta} {t("request.minShort", "min")} {t("request.away", "away")}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Fare summary — only shown when we have an estimate but no per-tier card above (i.e., not taxi or bus) */}
          {isTaxi && country === "TR" && (
            <div
              className="mb-3 text-[11px] leading-relaxed text-white/70 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2"
              data-testid="istanbul-taxi-tariff-notice"
            >
              {t(
                "request.istanbulTaxiTariffNotice",
                "Drivons Taxi pricing in Istanbul follows the current official Istanbul taxi tariff. The minimum fare is 230 TRY."
              )}
            </div>
          )}
          {!isTaxi && !isBus && (
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 mb-3" data-testid="fare-preview">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50">{t("request.farePreview", "Estimate")}</div>
                {fareBusy && <Loader2 size={12} className="animate-spin text-white/50" />}
              </div>
              {!pickup.lat && (
                <div className="text-[12px] text-white/50">{t("request.setPickupFirst", "Set pickup to see fare estimate")}</div>
              )}
              {pickup.lat && fareErr && (
                <div className="text-[12px] text-brand-warn" data-testid="fare-error">{fareErr}</div>
              )}
              {pickup.lat && !fareErr && fareHint && (
                <div className="text-[12px] text-white/60" data-testid="fare-hint">{fareHint}</div>
              )}
              {pickup.lat && !fareErr && !fareHint && fare && (
                <>
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold text-white tabular-nums" data-testid="fare-total">{fmtFare(fare.estimate)}</div>
                    {fare.method === "distance" && (
                      <div className="text-[11px] text-white/60" data-testid="fare-distance">· {fare.distance_km} km</div>
                    )}
                    {fare.country && (
                      <div className="ms-auto text-[9px] uppercase font-bold tracking-widest bg-electric-teal/10 border border-electric-teal/30 text-electric-teal rounded-full px-2 py-0.5" data-testid="fare-country">
                        {fare.country} · {(fare.currency || "usd").toUpperCase()}
                      </div>
                    )}
                  </div>
                  {fare.method === "distance" && (() => {
                    // Guard against `NaN/km × 0 km` — the historical bug
                    // came from reading the wrong breakdown field name
                    // (`per_km_rate` instead of `per_km`) and a missing
                    // km value before a route is picked. Only render the
                    // breakdown once both numbers are real.
                    const base = fare.breakdown?.base_fare;
                    const perKm = fare.breakdown?.per_km ?? fare.breakdown?.per_km_rate;
                    const km = fare.distance_km ?? fare.breakdown?.distance_km;
                    const ready = Number.isFinite(base) && Number.isFinite(perKm) && Number.isFinite(km) && km > 0;
                    return (
                      <div className="mt-1 text-[11px] text-white/50" data-testid="fare-breakdown">
                        {ready
                          ? <>{t("request.base", "Base")} {fmtFare(base)} + {fmtFare(perKm)}/km × {km.toFixed(2)} km</>
                          : t("request.fareCalcPending", "Price will be calculated once a destination is set")}
                      </div>
                    );
                  })()}
                  {/* Crane V1 — dedicated breakdown when the matched rule
                      is `base_km_hourly`. Shows exact line items so the
                      customer sees where every shekel goes. */}
                  {fare.breakdown?.formula === "base_km_hourly" && (
                    <div
                      className="mt-3 rounded-2xl border border-amber-gold/25 bg-amber-gold/[0.04] p-3 space-y-1.5 text-[12px] text-white/85"
                      data-testid="crane-fare-breakdown"
                    >
                      <div className="flex justify-between">
                        <span className="text-white/60">{t("crane.brBase", "Base (first 2 h)")}</span>
                        <span className="tabular-nums" data-testid="crane-br-base">{fmtFare(fare.breakdown.base_fare)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">
                          {t("crane.brExtraHours", "Extra hours")} · {fare.breakdown.extra_hours} × {fmtFare(fare.breakdown.hourly_extension_rate)}
                        </span>
                        <span className="tabular-nums" data-testid="crane-br-extra-hours">{fmtFare(fare.breakdown.extra_hour_cost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">
                          {t("crane.brExtraKm", "Extra km")} · {fare.breakdown.chargeable_km} × {fmtFare(fare.breakdown.per_km)} ({t("crane.brIncluded", "first")} {fare.breakdown.included_km ?? 0} km {t("crane.brFree", "free")})
                        </span>
                        <span className="tabular-nums" data-testid="crane-br-extra-km">{fmtFare(fare.breakdown.km_cost)}</span>
                      </div>
                      <div className="h-px bg-white/10 my-1" />
                      <div className="flex justify-between font-semibold text-white">
                        <span>{t("crane.brTotal", "Total")}</span>
                        <span className="tabular-nums" data-testid="crane-br-total">{fmtFare(fare.estimate)}</span>
                      </div>
                      {fare.breakdown.commission_percent != null && (
                        <div className="flex justify-between text-[11px] text-white/45">
                          <span>{t("crane.brCommission", "Drivons commission")} ({fare.breakdown.commission_percent}%)</span>
                          <span className="tabular-nums" data-testid="crane-br-commission">{fmtFare(fare.breakdown.drivon_commission)}</span>
                        </div>
                      )}
                      {fare.breakdown.driver_payout != null && (
                        <div className="flex justify-between text-[11px] text-white/45">
                          <span>{t("crane.brPayout", "Supplier payout")}</span>
                          <span className="tabular-nums" data-testid="crane-br-payout">{fmtFare(fare.breakdown.driver_payout)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Category-specific option pickers */}
          {isTire && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="tire"
              value={tireOption}
              onChange={setTireOption}
              pricingContext={optionPricingContext}
              testId="tire-option-grid"
            />
          )}
          {isTire && (
            <div className="mb-3" data-testid="tire-size-block">
              <input type="text" value={tireSize} onChange={(e) => setTireSize(e.target.value.slice(0, 30))}
                placeholder={t("request.tireSizePh", "Tire size (e.g. 205/55 R16)")}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal font-mono"
                data-testid="tire-size-input" />
              <div className="text-[10px] text-white/45 mt-1">{t("request.tireSizeHint", "Sharing your tire size helps the provider bring the right replacement.")}</div>
            </div>
          )}
          {isCarwash && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="carwash"
              value={carwashOption}
              onChange={setCarwashOption}
              pricingContext={optionPricingContext}
              testId="carwash-option-grid"
            />
          )}
          {isCrane && (
            <div className="mb-4" data-testid="crane-band-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.craneBand", "Crane boom height")}</div>
              <div className="-mx-4 px-4">
                <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x pb-1">
                  {[
                    { id: "crane_12_18m",   label: "12–18 m", subtitle: t("crane.small", "Compact") },
                    { id: "crane_18_24m",   label: "18–24 m", subtitle: t("crane.standard", "Standard") },
                    { id: "crane_24_42m",   label: "24–42 m", subtitle: t("crane.large", "Large") },
                    { id: "crane_42m_plus", label: "42 m+",   subtitle: t("crane.heavy", "Heavy-lift") },
                  ].map((tt) => {
                    const selected = craneBand === tt.id;
                    return (
                      <button
                        key={tt.id}
                        onClick={() => setCraneBand(tt.id)}
                        className={`snap-center flex-shrink-0 w-[128px] rounded-2xl p-2.5 text-start border transition-all duration-200 relative overflow-hidden ${
                          selected
                            ? "bg-amber-gold/[0.08] border-amber-gold ring-gold-glow scale-[1.02]"
                            : "bg-white/[0.04] border-white/10 hover:border-white/25"
                        }`}
                        data-testid={`crane-band-${tt.id}`}
                      >
                        <div className="h-11 flex items-center justify-center">
                          <Construction size={36} className={selected ? "text-amber-gold" : "text-white/80"} strokeWidth={1.5} />
                        </div>
                        <div className="text-[12px] font-bold text-white truncate leading-tight">{tt.label}</div>
                        <div className="text-[10px] text-white/50 mt-0.5 truncate">{tt.subtitle}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {isCrane && (
            <div className="mb-4" data-testid="crane-hours-picker">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
                {t("request.craneHours", "Job duration (hours)")}
              </div>
              <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3">
                <button
                  type="button"
                  onClick={() => setBookedHours((h) => Math.max(2, Number(h || 2) - 1))}
                  disabled={Number(bookedHours || 2) <= 2}
                  className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/10 text-white text-xl font-bold flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-amber-gold transition-colors"
                  data-testid="crane-hours-dec"
                  aria-label="decrease hours"
                >−</button>
                <div className="flex-1 text-center">
                  <div className="text-3xl font-bold text-white tabular-nums" data-testid="crane-hours-value">
                    {Number(bookedHours || 2)}
                  </div>
                  <div className="text-[10px] text-white/50 mt-0.5">
                    {t("request.craneHoursHint", "Min 2 h · first 2 h included in base")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBookedHours((h) => Math.min(12, Number(h || 2) + 1))}
                  disabled={Number(bookedHours || 2) >= 12}
                  className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/10 text-white text-xl font-bold flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-amber-gold transition-colors"
                  data-testid="crane-hours-inc"
                  aria-label="increase hours"
                >+</button>
              </div>
            </div>
          )}
          {isTowing && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="towing"
              value={selectedOption}
              onChange={setSelectedOption}
              pricingContext={optionPricingContext}
              testId="towing-option-grid"
            />
          )}
          {isMoving && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="moving"
              value={selectedOption}
              onChange={setSelectedOption}
              pricingContext={optionPricingContext}
              testId="moving-option-grid"
            />
          )}
          {isLuxury && (
            <LuxuryClassPicker
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              pickupLat={pickup?.lat ?? null}
              pickupLng={pickup?.lng ?? null}
              currency={fare?.currency}
              vehicleClass={luxuryVehicleClass}
              onClassChange={(cls) => {
                setLuxuryVehicleClass(cls);
                // First-time class pick → default to hourly with min hours 2
                if (!luxuryRentalType || luxuryRentalType === "per_trip") {
                  setLuxuryRentalType("hourly");
                  if (!luxuryRentalDuration) setLuxuryRentalDuration(2);
                }
              }}
              rentalType={luxuryRentalType === "per_trip" ? null : luxuryRentalType}
              onRentalTypeChange={setLuxuryRentalType}
              rentalDuration={luxuryRentalDuration}
              onRentalDurationChange={setLuxuryRentalDuration}
              expectedKm={luxuryExpectedKm}
              onExpectedKmChange={setLuxuryExpectedKm}
            />
          )}
          {isLuxury && (
            <LuxuryVehiclePicker
              pickupLat={pickup?.lat ?? null}
              pickupLng={pickup?.lng ?? null}
              currency={fare?.currency}
              selectedVehicleId={luxuryVehicleId}
              rentalType={luxuryRentalType}
              rentalDuration={luxuryRentalDuration}
              onSelect={(v) => {
                setLuxuryVehicleId(v.vehicle_id || `supplier:${v.supplier_id}`);
                if (v.model) setLuxuryVehicleModel(v.model);
              }}
              onRentalTypeChange={(m) => {
                setLuxuryRentalType(m);
                if (m === "per_trip") setLuxuryRentalDuration(null);
                if (m === "hourly" && !luxuryRentalDuration) setLuxuryRentalDuration(2);
                if (m === "daily" && !luxuryRentalDuration) setLuxuryRentalDuration(1);
              }}
              onRentalDurationChange={setLuxuryRentalDuration}
            />
          )}
          {isMechanic && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="mechanic"
              value={selectedOption}
              onChange={setSelectedOption}
              pricingContext={optionPricingContext}
              testId="mechanic-option-grid"
            />
          )}
          {isMechanic && (
            <div className="mb-3" data-testid="mechanic-fault-note">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.faultNote", "Describe the fault")}</div>
              <textarea rows={3} value={faultNote}
                onChange={(e) => setFaultNote(e.target.value.slice(0, 500))}
                placeholder={t("request.faultNotePh", "e.g. Engine won't start, warning light on, unusual noise…")}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-brand-accent resize-none"
                data-testid="fault-note-input" />
            </div>
          )}
          {isDelivery && (
            <ServiceOptionGrid
              country={(fare?.country || countryFromCoords(pickup?.lat, pickup?.lng) || getGeo()?.country || country || "IL").toUpperCase()}
              category="delivery"
              value={selectedOption}
              onChange={setSelectedOption}
              pricingContext={optionPricingContext}
              testId="delivery-option-grid"
            />
          )}
          {isDelivery && (
            <div className="mb-3 bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-2" data-testid="delivery-details">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50">{t("request.deliveryDetails", "Delivery address details")}</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="text" value={deliveryDetails.apartment} onChange={(e) => setDeliveryDetails((p) => ({...p, apartment: e.target.value.slice(0,30)}))} placeholder={t("request.apartment", "Apartment #")} className="bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal" data-testid="delivery-apartment" />
                <input type="text" value={deliveryDetails.floor} onChange={(e) => setDeliveryDetails((p) => ({...p, floor: e.target.value.slice(0,15)}))} placeholder={t("request.floor", "Floor")} className="bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal" data-testid="delivery-floor" />
                <input type="text" value={deliveryDetails.building} onChange={(e) => setDeliveryDetails((p) => ({...p, building: e.target.value.slice(0,80)}))} placeholder={t("request.building", "Building / block")} className="bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal" data-testid="delivery-building" />
                <input type="text" value={deliveryDetails.gate_code} onChange={(e) => setDeliveryDetails((p) => ({...p, gate_code: e.target.value.slice(0,20)}))} placeholder={t("request.gateCode", "Gate code (if any)")} className="bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal" data-testid="delivery-gate" />
              </div>
              <input type="tel" value={deliveryDetails.contact_phone} onChange={(e) => setDeliveryDetails((p) => ({...p, contact_phone: e.target.value.slice(0,30)}))} placeholder={t("request.contactPhone", "Recipient phone")} className="w-full bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal" data-testid="delivery-phone" />
              <div className="flex gap-2 pt-1">
                {[{v:"leave_at_door",l:t("request.leaveAtDoor","Leave at door")},{v:"leave_in_lobby",l:t("request.leaveInLobby","Leave in lobby")},{v:"hand_to_recipient",l:t("request.handToRecipient","Hand to recipient")}].map((o) => (
                  <button key={o.v} onClick={() => setDeliveryDetails((p) => ({...p, instructions: o.v}))}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold border transition-all ${deliveryDetails.instructions === o.v ? "bg-electric-teal text-obsidian border-electric-teal" : "bg-white/[0.04] text-white/70 border-white/10"}`}
                    data-testid={`delivery-inst-${o.v}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Driver notes — universal, shown for every category */}
          <div className="mb-3" data-testid="driver-notes-section">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.driverNotes", "Notes for the driver")}</div>
            <textarea rows={2} value={driverNotes}
              onChange={(e) => setDriverNotes(e.target.value.slice(0, 500))}
              placeholder={t("request.driverNotesPh", "Anything the driver should know (gate code, meeting point, fragile items…)")}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal resize-none"
              data-testid="driver-notes-input" />
          </div>

          {/* Discount code */}
          <div className="mb-3" data-testid="discount-section">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("request.discountCode", "Discount code")}</div>
            <div className="flex gap-2">
              <input type="text" value={discountCode}
                onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountErr(null); setDiscountPreview(null); }}
                placeholder={t("request.discountPh", "Enter code (e.g. WELCOME10)")}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-amber-gold font-mono"
                data-testid="discount-input" />
              <button
                type="button"
                onClick={async () => {
                  if (!discountCode.trim() || !fare) return;
                  try {
                    const { data } = await api.get("/discount-codes/preview", {
                      params: { code: discountCode.trim(), category, pre_total: fare.estimate },
                    });
                    setDiscountPreview(data); setDiscountErr(null);
                    toast.success(t("request.discountApplied", "Discount applied"));
                  } catch (e) {
                    setDiscountPreview(null);
                    setDiscountErr(e?.response?.data?.detail || "Invalid code");
                  }
                }}
                disabled={!discountCode.trim() || !fare}
                className="bg-amber-gold text-obsidian rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-40 inline-flex items-center gap-1"
                data-testid="discount-apply-btn">
                <Tag size={12} /> {t("request.apply", "Apply")}
              </button>
            </div>
            {discountErr && <div className="mt-1 text-[11px] text-brand-warn" data-testid="discount-error">{discountErr}</div>}
            {discountPreview && (
              <div className="mt-1 text-[11px] text-amber-gold font-semibold" data-testid="discount-preview">
                −{fmtFare(discountPreview.discount_amount)} · {t("request.newTotal", "New total")}: {fmtFare(discountPreview.new_total)}
              </div>
            )}
          </div>

          {/* Payment method row */}
          <div className="mb-2" data-testid="payment-section">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">{t("payment.method", "Payment")}</div>
            {/* iOS Safari fix: the previous row was `overflow-x-auto`, which
                on iPhone caused two problems:
                  1. WebKit's 300ms pan-detection swallowed tap events on
                     chips positioned mid-row.
                  2. When Apple Pay + Google Pay chips were shown (iOS
                     Safari sets hasApplePay=true), the Card chip could
                     scroll off-screen horizontally, so users tapping
                     where they *thought* Card was actually hit an
                     empty region beyond the visible chips.
                We now use `flex-wrap` — chips wrap to the next line on
                narrow viewports, no scroll delay, no hidden chips. */}
            <div className="flex flex-wrap gap-2" data-testid="payment-chip-row">
              <PayChip icon={Banknote} selected={paymentMethod === "cash"} onClick={() => setPaymentMethod("cash")} label={t("payment.cash", "Cash")} testid="req-pay-cash" />
              <PayChip
                icon={WalletIcon}
                selected={paymentMethod === "wallet"}
                onClick={() => walletInsufficient ? null : setPaymentMethod("wallet")}
                label={`${t("payment.wallet", "Wallet")} · ${formatMoney(walletBalance, walletCurrency, { maxFractionDigits: 0 })}`}
                testid="req-pay-wallet"
                disabled={walletInsufficient}
              />
              {/* Card, Apple Pay, Google Pay and PayPal all send `payment_method="paypal"`
                  to the backend — PayPal Checkout's hosted page renders each of
                  these funding sources as a first-class guest option on the
                  buyer's actual device. Chips are separate here for UX
                  clarity. Apple/Google Pay chips render only when the current
                  device advertises support (see hasApplePay / hasGooglePay). */}
              <PayChip icon={CreditCard} selected={paymentMethod === "card"}
                onClick={() => {
                  // TEMP DEBUG (safe, no PII): confirms the Card chip's
                  // click handler is actually reached on the real iPhone.
                  // Remove after the iOS debug pass concludes.
                  try {
                    // eslint-disable-next-line no-console
                    console.log("[Drivons][PayChip] Card tap →",
                      { touchAction: "manipulation", ua: navigator.userAgent.slice(0,80), ts: Date.now() });
                  } catch {}
                  setPaymentMethod("card");
                }}
                label={t("payment.cardChip", "Card")} testid="req-pay-card" />
              {hasApplePay && (
                <PayChip icon={AppleIcon} selected={paymentMethod === "applepay"} onClick={() => setPaymentMethod("applepay")} label="Apple Pay" testid="req-pay-applepay" />
              )}
              {hasGooglePay && (
                <PayChip icon={GoogleIcon} selected={paymentMethod === "googlepay"} onClick={() => setPaymentMethod("googlepay")} label="Google Pay" testid="req-pay-googlepay" />
              )}
              <PayChip icon={WalletIcon} selected={paymentMethod === "paypal"} onClick={() => setPaymentMethod("paypal")} label="PayPal" testid="req-pay-paypal" />
            </div>
            {isPaypalFlow && (
              <div className="mt-2 text-[11px] text-white/50" data-testid="pay-card-hint">
                {paymentMethod === "applepay"
                  ? t("payment.applepayHint", "You'll be taken to PayPal to authorise with Apple Pay.")
                  : paymentMethod === "googlepay"
                  ? t("payment.googlepayHint", "You'll be taken to PayPal to authorise with Google Pay.")
                  : t("payment.cardViaPaypal", "Visa · Mastercard · Amex accepted — processed securely by PayPal. No PayPal account required.")}
              </div>
            )}
            {walletInsufficient && paymentMethod === "wallet" && (
              <div className="mt-2 text-[11px] text-brand-warn" data-testid="wallet-insufficient-req">
                {t("payment.walletInsufficient", "Wallet balance is not enough for this trip.")}
              </div>
            )}
          </div>
        </div>

        {/* Route Anomaly Guard — warns the rider if the pickup + dropoff
            look ambiguous (very long trip, likely wrong country match from
            Nominatim). Renders directly above the Request CTA. */}
        {(() => {
          if (!fare?.distance_km || !pickup.address || !dropoff.address) return null;
          const km = fare.distance_km;
          const suspicious = km >= 200;
          if (!suspicious) return null;
          const tail = (s) => (s || "").split(",").pop().trim().toLowerCase();
          const p = tail(pickup.address);
          const d = tail(dropoff.address);
          const differentCountry = p && d && p !== d;

          // Auto-correct: re-search the dropoff biased to the pickup's country.
          // If we find a better local match, offer it as a one-tap fix.
          const autoCorrectDropoff = async () => {
            try {
              const parts = pickup.address.split(",").map((s) => s.trim());
              // Search seeded with the pickup's country appended to the query
              const dropoffMain = dropoff.address.split(",")[0];
              const candidates = await searchPlaces(`${dropoffMain} ${parts[parts.length - 1]}`);
              // Only accept a candidate whose distance to pickup < 200km
              const _dist = (a, b) => {
                const R = 6371, toRad = (v) => (v * Math.PI) / 180;
                const dLat = toRad(b.lat - a.lat);
                const dLng = toRad(b.lng - a.lng);
                const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
                return 2 * R * Math.asin(Math.sqrt(s));
              };
              const better = candidates.find((c) => _dist({ lat: pickup.lat, lng: pickup.lng }, c) < 200);
              if (better) {
                setDropoff({ address: better.label, lat: better.lat, lng: better.lng });
                toast.success(t("request.anomalyFixed", "Corrected drop-off to a local match"));
              } else {
                toast.error(t("request.anomalyNoFix", "No closer match found — try picking manually"));
              }
            } catch (e) {
              toast.error(t("request.anomalyNoFix", "No closer match found — try picking manually"));
            }
          };

          return (
            <div
              className="mx-4 mb-3 rounded-2xl border border-amber-500/40 bg-amber-500/[0.08] p-3 flex items-start gap-3"
              data-testid="route-anomaly-warn"
            >
              <div className="w-8 h-8 rounded-full bg-amber-500/25 text-amber-300 flex items-center justify-center shrink-0 mt-0.5">
                ⚠️
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">
                  {t("request.anomalyEyebrow", "Long trip — double-check")}
                </div>
                <div className="text-[12px] font-bold text-white mt-0.5">
                  {differentCountry
                    ? t("request.anomalyCross", "This trip appears to cross countries ({{km}} km). One of your addresses may be wrong.", { km: Math.round(km) })
                    : t("request.anomalyFar", "This trip is {{km}} km — that's much farther than most rides. Confirm both addresses are correct.", { km: Math.round(km) })}
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {differentCountry && (
                    <button
                      type="button"
                      onClick={autoCorrectDropoff}
                      className="text-[10px] uppercase font-black text-obsidian bg-amber-gold hover:bg-amber-gold/90 rounded-full px-2.5 py-1 transition inline-flex items-center gap-1"
                      data-testid="route-anomaly-autofix"
                    >
                      ✨ {t("request.anomalyAutoFix", "Auto-correct drop-off")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setPickup({ address: "", lat: null, lng: null })}
                    className="text-[10px] uppercase font-black text-amber-300 hover:text-amber-200 border border-amber-500/40 hover:bg-amber-500/10 rounded-full px-2.5 py-1 transition"
                    data-testid="route-anomaly-fix-pickup"
                  >
                    {t("request.anomalyFixPickup", "Fix pickup")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDropoff({ address: "", lat: null, lng: null })}
                    className="text-[10px] uppercase font-black text-amber-300 hover:text-amber-200 border border-amber-500/40 hover:bg-amber-500/10 rounded-full px-2.5 py-1 transition"
                    data-testid="route-anomaly-fix-dropoff"
                  >
                    {t("request.anomalyFixDropoff", "Fix drop-off")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Sticky CTA */}
        <div className="border-t border-white/10 bg-obsidian/95 backdrop-blur-md p-4 pb-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
          <button
            onClick={submit}
            disabled={busy || (paymentMethod === "wallet" && walletInsufficient)}
            className="w-full h-14 rounded-2xl bg-white text-black font-bold text-[15px] transition-all active:scale-[0.98] hover:bg-white/95 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 tracking-tight"
            data-testid="request-now-btn"
          >
            {busy ? <Loader2 size={17} className="animate-spin" /> : (scheduleMode === "later" ? <Calendar size={17} /> : <Send size={17} />)}
            {(() => {
              const label = scheduleMode === "later"
                ? t("request.ctaSchedule", "Reserve Ride")
                : (isTaxi ? t("request.ctaChoose", `Request ${t(`taxi.${taxiType}`, taxiType)}`)
                  : isBus ? t("request.ctaChooseBus", `Book ${t(`bus.${busType}`, busType)}`)
                  : t("request.cta", "Request Now"));
              return fare ? `${label} · ${fmtFare(fare.estimate)}` : label;
            })()}
          </button>
          <div className="mt-2 text-center text-[10px] text-white/50 tracking-wide">
            {scheduleMode === "later"
              ? t("request.noteScheduled", "We'll match a nearby driver ~10 min before your booking time.")
              : t("request.note", "Nearest available drivers will be notified. First to accept becomes your driver.")}
          </div>
        </div>
      </div>

      {/* iOS debug tracer — only renders when ?debug=tap is in the URL */}
      <IosTapTracer />

      {/* In-app Advanced Card Fields (ACDC) — opens for Israel/Card only.
          The modal handles order creation, 3DS challenge, capture, and
          fallback to the redirect flow if ACDC becomes unavailable
          server-side. */}
      {pendingCardCheckout && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="paypal-card-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPendingCardCheckout(null); }}
        >
          <div className="w-full max-w-md bg-ink-surface rounded-t-3xl sm:rounded-3xl border border-ink-border p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-white font-black text-[16px]">Pay with Card</div>
                <div className="text-white/60 text-[11px]">Amount: {Number(pendingCardCheckout.amount).toFixed(2)} {pendingCardCheckout.currency}</div>
              </div>
              <button
                type="button"
                className="text-white/60 hover:text-white text-[13px]"
                onClick={() => setPendingCardCheckout(null)}
                data-testid="paypal-card-modal-close"
              >Close</button>
            </div>
            <PayPalCardCheckout
              orderId={pendingCardCheckout.orderId}
              amount={pendingCardCheckout.amount}
              currency={pendingCardCheckout.currency}
              onSuccess={(cap) => {
                // Snapshot before clearing state — same Safari JIT hazard
                // as the fallback path below.
                const snap = pendingCardCheckout;
                setPendingCardCheckout(null);
                toast.success(t("request.paypal.paid", "Payment received"));
                nav(`/order/${cap.order_id || snap.orderId}`);
              }}
              onFallback={async () => {
                // Snapshot the pending order id BEFORE clearing state —
                // Safari's JIT will not reliably keep `pendingCardCheckout`
                // alive across the `await` after we set it to null.
                const snap = pendingCardCheckout;
                setPendingCardCheckout(null);
                toast(t("request.paypal.fallback",
                  "In-app card not available — continuing on PayPal"));
                try {
                  const { data: pp } = await api.post("/payments/paypal/create", {
                    order_id: snap.orderId,
                    origin_url: window.location.origin,
                    confirmed_conversion: false,
                  });
                  window.location.assign(pp.approve_url);
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              onCancel={() => setPendingCardCheckout(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PayChip({ icon: Icon, selected, onClick, label, testid, disabled }) {
  // iOS/Safari fix — inside an overflow-x-auto scrollable row, WebKit
  // waits 300 ms and can swallow a tap when it decides the touch is a
  // horizontal pan. `touch-action: manipulation` opts out of that
  // detection, and the WebkitTapHighlightColor removes the default
  // grey flash so the tap feels immediate. Applied here (not on the
  // parent row) so it only affects the chips — the row itself still
  // scrolls freely.
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type="button"
      style={{
        touchAction: "manipulation",
        WebkitTapHighlightColor: "rgba(0,0,0,0)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 border text-[12px] font-semibold transition-colors select-none ${
        selected
          ? "bg-white text-obsidian border-white"
          : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/25 active:bg-white/15"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      <Icon size={13} />
      <span>{label}</span>
    </button>
  );
}

// Apple + Google logo glyphs (lucide doesn't ship brand marks). Kept
// small and monochrome so they inherit the chip's colour — no branded
// palette, no colour mismatch when the chip is selected (black on white)
// versus unselected (white on transparent).
function AppleIcon({ size = 13, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...rest}>
      <path d="M17.05 13.02c0-2.87 2.34-4.24 2.45-4.31-1.34-1.96-3.42-2.23-4.16-2.26-1.77-.18-3.45 1.04-4.35 1.04-.9 0-2.29-1.02-3.76-.99-1.94.03-3.73 1.13-4.72 2.86-2.02 3.5-.52 8.68 1.45 11.52.96 1.38 2.11 2.94 3.62 2.88 1.45-.06 2.01-.94 3.77-.94 1.76 0 2.26.94 3.8.91 1.57-.03 2.56-1.4 3.52-2.79 1.11-1.6 1.57-3.16 1.6-3.25-.03-.02-3.06-1.18-3.09-4.67zM14.35 4.24c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.54.77-3.36 1.73-.74.85-1.39 2.22-1.22 3.53 1.28.1 2.6-.65 3.39-1.6z" />
    </svg>
  );
}
function GoogleIcon({ size = 13, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...rest}>
      <path d="M21.35 11.1H12v2.9h5.35c-.24 1.4-1.7 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.95S8.78 6.2 12 6.2c1.83 0 3.06.78 3.76 1.44l2.56-2.47C16.85 3.65 14.63 2.6 12 2.6 6.94 2.6 2.85 6.69 2.85 11.75 2.85 16.81 6.94 20.9 12 20.9c6.94 0 9.55-4.87 9.55-7.9 0-.53-.05-.98-.2-1.9z" />
    </svg>
  );
}

function useOrderStatus(orderId, pollMs = 3000) {
  const [status, setStatus] = useState(null);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!orderId) return;
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/dispatch-status`);
        if (!mounted) return;
        setStatus(data);
      } catch (_) {}
    };
    tick();
    const poll = setInterval(tick, pollMs);
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    // Real-time WS listener — refetch on any order-status push. Keeps poll
    // as fallback so a dropped WS doesn't stall the UI.
    let ws = null;
    try {
      const token = localStorage.getItem("haulhub_token");
      if (token) {
        const wsUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/dispatch?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m?.type === "order_status" && m?.order_id === orderId) {
              tick();
            }
          } catch (_) {}
        };
      }
    } catch (_) {}
    return () => {
      mounted = false;
      clearInterval(poll);
      clearInterval(timer);
      if (ws) try { ws.close(); } catch (_) {}
    };
  }, [orderId, pollMs]);
  const refresh = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/dispatch-status`);
      setStatus(data);
    } catch (_) {}
  };
  return { status, seconds, refresh };
}

function DispatchingScreen({ order, onCancelled }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { status, seconds, refresh } = useOrderStatus(order.order_id);
  const [cancelling, setCancelling] = useState(false);
  const [rebroadcasting, setRebroadcasting] = useState(false);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.patch(`/orders/${order.order_id}/status`, { status: "cancelled" });
      toast.success(t("request.cancelled", "Request cancelled"));
      onCancelled && onCancelled();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const rebroadcast = async () => {
    setRebroadcasting(true);
    try {
      await api.post(`/orders/${order.order_id}/rebroadcast`);
      toast.success(t("request.rebroadcasted", "Re-notified nearby drivers"));
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRebroadcasting(false);
    }
  };

  const isAccepted = status?.status === "accepted" || status?.status === "in_progress";
  const isCompleted = status?.status === "completed";
  const isPending = !isAccepted && !isCompleted && status?.status !== "cancelled";
  const timedOut = isPending && seconds >= 60;
  const noDriversLeft = isPending && (status?.drivers_pending ?? 1) === 0;

  return (
    <div className="fixed inset-0 bg-obsidian overflow-y-auto" data-testid="dispatching-screen">
      <div className="max-w-[600px] mx-auto p-4 pt-20 pb-24">
        {/* Radar animation card */}
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-8 text-center relative overflow-hidden">
          {isPending && (
            <>
              <div className="relative mx-auto w-28 h-28 flex items-center justify-center">
                <div className="radar-ring" style={{ animationDelay: "0s" }} />
                <div className="radar-ring" style={{ animationDelay: "0.8s" }} />
                <div className="radar-ring" style={{ animationDelay: "1.6s" }} />
                <div className="relative w-16 h-16 rounded-full bg-electric-teal/15 border border-electric-teal/40 flex items-center justify-center shadow-teal">
                  <Bell size={26} className={timedOut ? "text-brand-warn" : "text-electric-teal"} />
                </div>
              </div>
              <div className="mt-4 text-[18px] font-bold text-white tracking-tight" data-testid="dispatching-title">
                {timedOut
                  ? t("request.noDrivers", "No drivers responded yet")
                  : t("request.looking", "Looking for a nearby driver…")}
              </div>
              <div className="mt-1 text-[12px] text-white/60 tabular-nums">
                {status?.drivers_alerted ?? 0} {t("request.driversAlerted", "drivers alerted")} · {seconds}s
                {timedOut && ` · ${status?.drivers_pending ?? 0} ${t("request.stillDeciding", "still deciding")}`}
              </div>
              {timedOut && (
                <div className="mt-2 text-[11px] text-white/60 px-2" data-testid="no-drivers-note">
                  {noDriversLeft
                    ? t("request.allDeclined", "Every nearby driver has declined or ignored. Try broadcasting to more drivers or cancel and try later.")
                    : t("request.stillWaiting", "Still waiting for a driver to accept. You can wait, re-broadcast to nearby drivers, or cancel.")}
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2 justify-center">
                {timedOut && (
                  <button
                    onClick={rebroadcast}
                    disabled={rebroadcasting}
                    className="bg-amber-gold text-obsidian rounded-full px-4 py-2 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 shadow-gold"
                    data-testid="rebroadcast-btn"
                  >
                    {rebroadcasting ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                    {t("request.rebroadcast", "Re-notify nearby drivers")}
                  </button>
                )}
                <button
                  onClick={cancel}
                  disabled={cancelling}
                  className="bg-red-500/15 border border-red-500/40 text-red-300 rounded-full px-4 py-2 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="cancel-request-btn"
                >
                  <X size={12} /> {t("request.cancel", "Cancel request")}
                </button>
              </div>
            </>
          )}
          {isAccepted && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-electric-teal/15 border border-electric-teal/40 flex items-center justify-center">
                <CheckCircle2 size={26} className="text-electric-teal" />
              </div>
              <div className="mt-3 text-[18px] font-bold text-white" data-testid="driver-assigned">
                {t("request.driverFound", "Driver assigned!")}
              </div>
              <div className="mt-1 text-[12px] text-white/60">{status?.supplier_name}</div>
              {status?.verification_code && status?.status === "accepted" && (
                <div className="mt-5 mx-auto max-w-xs bg-gradient-to-br from-amber-gold/20 to-amber-gold/5 border-2 border-amber-gold/60 rounded-2xl p-4 shadow-gold" data-testid="verification-code-card">
                  <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-amber-gold/90 text-center">
                    {t("request.showThisCode", "Show this code to your driver")}
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-2 font-mono text-white" data-testid="verification-code-digits">
                    {status.verification_code.split("").map((d, i) => (
                      <span key={i} className="inline-flex items-center justify-center w-11 h-14 bg-obsidian border border-amber-gold/40 rounded-xl text-[28px] font-bold tabular-nums">
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] text-white/60 text-center leading-relaxed">
                    {t("request.codeHint", "Your driver will ask for this 4-digit code before starting the trip.")}
                  </div>
                </div>
              )}
              {status?.verified_at && status?.status === "in_progress" && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-electric-teal/10 border border-electric-teal/40 rounded-full px-3 py-1 text-[11px] font-bold text-electric-teal" data-testid="trip-verified-badge">
                  <CheckCircle2 size={12} /> {t("request.tripVerified", "Trip verified")}
                </div>
              )}
            </>
          )}
          {isCompleted && (
            <>
              <CheckCircle2 size={26} className="mx-auto text-electric-teal" />
              <div className="mt-3 text-[18px] font-bold text-white">{t("request.completed", "Trip completed")}</div>
            </>
          )}
        </div>

        {(isAccepted || isCompleted) && (
          <>
            <DriverCard profile={status?.driver_profile} className="mt-4" />
            <div className="mt-4">
              <LiveTrackingMap orderId={order.order_id} height={280} />
            </div>
            <div className="mt-3">
              <ChatPanel orderId={order.order_id} peerName={status?.supplier_name} />
            </div>
            <button
              onClick={() => nav("/customer")}
              className="mt-4 w-full h-12 bg-white text-obsidian rounded-2xl font-bold text-sm"
              data-testid="go-to-dashboard-btn"
            >
              {t("request.viewInDashboard", "View in Dashboard")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
